import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react'
import {
	UseFormGetValues,
	UseFormReset,
	UseFormSetValue,
	UseFormWatch
} from 'react-hook-form'

import { UploadFormService } from '@/services/uploadForm.service'

import { getDefaultFormValues } from '@/features/FormUploader/utils/formDefaults'
import { IOption } from '@/types/selectOption.interface'
import { ILanguage } from '@/types/task.interface'
import {
	IFetchUploadForm,
	IPlatform,
	ISelectedPlatform,
	IUploaderForm,
	TPlatformLabel,
	TPlatformValue
} from '@/types/uploadForm.interface'

import { addPlatform } from '../actions/addPlatform'
import { deletePlatform } from '../actions/deletePlatform'
import { resetPlatformHeaderState } from '../helpers/resetPlatformHeaderState'

import { useFieldChange } from './useFieldChange'
import { useFormCompletion } from './useFormCompletion'
import { useFormData } from './useFormData'
import { useFormSubmit } from './useFormSubmit'
import { useMetadata } from './useMetadata'
import { useTags } from './useTags'

interface UseModalUploadFormControllerProps {
	uploaderFormGetValues: UseFormGetValues<IUploaderForm>
	uploaderFormWatch: UseFormWatch<IUploaderForm>
	uploaderFormSetValue: UseFormSetValue<IUploaderForm>
	uploaderFormReset: UseFormReset<IUploaderForm>
	taskId: string
	setIsUploaded: (isUploaded: boolean) => void
	onSuccess?: () => void
	setIsFormUploading: (value: boolean) => void
	firstPlatformLabel?: TPlatformLabel
	platformList?: IPlatform[]
	selectedTranscribingLang?: ILanguage | null
	setSelectedTranscribingLang?: Dispatch<SetStateAction<ILanguage | null>>
	selectedPlatform?: TPlatformValue | null
	setSelectedPlatform?: Dispatch<SetStateAction<TPlatformValue | null>>
	selectedPlatformList?: ISelectedPlatform[]
	setSelectedPlatformList?: Dispatch<SetStateAction<ISelectedPlatform[]>>
	setAvailableSourceLanguages?: Dispatch<SetStateAction<IOption[]>>
	refetchShotlist?: () => Promise<unknown>
}

export const useModalUploadFormController = ({
	uploaderFormGetValues,
	uploaderFormWatch,
	uploaderFormSetValue,
	uploaderFormReset,
	taskId,
	onSuccess,
	setIsUploaded,
	setIsFormUploading,
	firstPlatformLabel,
	platformList,
	selectedPlatform,
	setSelectedPlatform,
	selectedPlatformList,
	setSelectedPlatformList,
	setSelectedTranscribingLang,
	setAvailableSourceLanguages,
	refetchShotlist
}: UseModalUploadFormControllerProps) => {
	const selectedRegion = uploaderFormWatch('region')

	useEffect(() => {
		if (
			!firstPlatformLabel ||
			!platformList ||
			!setSelectedPlatform ||
			!setSelectedPlatformList
		)
			return

		if (selectedPlatformList && selectedPlatformList.length > 0) return

		const platform = platformList.find(p => p.label === firstPlatformLabel)
		if (!platform) return

		setSelectedPlatform(platform.value)
		setSelectedPlatformList([{ value: platform.value, headerState: 'idle' }])
	}, [
		firstPlatformLabel,
		platformList,
		selectedPlatformList,
		setSelectedPlatform,
		setSelectedPlatformList
	])

	useEffect(() => {
		const order = selectedPlatformList?.map(p => p.value) || []

		UploadFormService.sendFormData(taskId, {
			is_completed: false,
			platforms_order: order
		} as IFetchUploadForm)
	}, [taskId, selectedPlatformList])

	const tagsOptions = useTags(selectedPlatformList?.[0]?.value)
	const { categoriesOptions, regionsOptions, countriesOptions } = useMetadata()
	const countryOptions = countriesOptions[selectedRegion?.value]
	const { updatePlatformCompletion } = useFormCompletion()

	const getDefaultFormValuesCallback = useCallback(
		() => getDefaultFormValues(),
		[]
	)

	const clearFormFields = useCallback(() => {
		const defaultValues = getDefaultFormValues()
		Object.keys(defaultValues).forEach((key: string) => {
			const fieldKey = key as keyof IUploaderForm
			uploaderFormSetValue(fieldKey, defaultValues[fieldKey])
		})
	}, [uploaderFormSetValue])

	useFormData({
		taskId,
		selectedPlatform,
		uploaderFormGetValues,
		uploaderFormSetValue,
		getDefaultFormValues: getDefaultFormValuesCallback,
		clearFormFields,
		uploaderFormReset,
		updatePlatformCompletion
	})

	const handleFieldChange = useFieldChange({
		taskId,
		selectedPlatform,
		uploaderFormGetValues,
		uploaderFormSetValue,
		updatePlatformCompletion
	})

	const onSubmit = useFormSubmit({
		taskId,
		selectedPlatform,
		uploaderFormGetValues,
		updatePlatformCompletion,
		onSuccess,
		setIsUploaded,
		setIsFormUploading
	})

	const prevRegionRef = useRef<IOption | null>(null)
	useEffect(() => {
		if (
			prevRegionRef.current !== null &&
			prevRegionRef.current !== selectedRegion
		) {
			uploaderFormSetValue('country', null)
		}
		prevRegionRef.current = selectedRegion
	}, [selectedRegion, uploaderFormSetValue])

	const handleAddPlatform = async (value: TPlatformValue) => {
		try {
			setIsFormUploading(true)
			await addPlatform(
				taskId,
				value,
				setSelectedPlatformList!,
				setSelectedPlatform!,
				selectedPlatformList || []
			)
			await refetchShotlist?.()
		} catch (error) {
			console.log(error)
		} finally {
			setIsFormUploading(false)
		}
	}

	const handleDeletePlatform = async (value: TPlatformValue) => {
		try {
			await deletePlatform(
				value,
				taskId,
				selectedPlatform || null,
				selectedPlatformList || [],
				setSelectedPlatformList!,
				setSelectedPlatform!,
				setSelectedTranscribingLang!,
				setAvailableSourceLanguages!
			)
			await refetchShotlist?.()
		} catch (error) {
			console.log(error)
		}
	}

	const handleCloseErrorMessage = () => {
		if (!selectedPlatform || !setSelectedPlatformList) return

		resetPlatformHeaderState({
			selectedPlatform,
			setSelectedPlatformList
		})
	}

	const currentPlatform = selectedPlatform
		? selectedPlatformList?.find(p => p.value === selectedPlatform) ?? null
		: null

	const uploadButtonDisabled =
		currentPlatform?.headerState === 'processing' ||
		currentPlatform?.processing === 'autofill'

	return {
		selectedRegion,
		tagsOptions,
		categoriesOptions,
		regionsOptions,
		countryOptions,
		handleFieldChange,
		onSubmit,
		handleAddPlatform,
		handleDeletePlatform,
		handleCloseErrorMessage,
		uploadButtonDisabled
	}
}
